import { ChangeDetectionStrategy, Component, Input, OnInit } from '@angular/core';
import * as moment from 'moment';
import { Frac, FracGraphSocketUpdate, FracStorage } from '~lmo/models/frac.model';
import { loadPluralMapping } from '~lmo/pluralMappings';

interface PreciseDiff {
  days: number;
  firstDateWasLater: boolean;
  hours: number;
  minutes: number;
  months: number;
  seconds: number;
  years: number;
}

const dateOrder: (keyof PreciseDiff)[] = ['years', 'months', 'days', 'hours', 'minutes'];

const substitutes: Partial<Record<keyof PreciseDiff, string>> = { months: 'mo' };

function ago(dateAsString: string): string {
  const asMoment = moment(dateAsString);
  if (asMoment.isValid) {
    const preciseDiff: PreciseDiff = (moment as any).preciseDiff(moment(), asMoment, true);
    if (!preciseDiff.firstDateWasLater) {
      return '-';
    }
    const result = [];
    let dateOrderIndex = 0;
    while (dateOrderIndex < dateOrder.length && result.length < 1) {
      const key = dateOrder[dateOrderIndex];
      if (preciseDiff[key] > 0) {
        const keyName = substitutes[key] ? substitutes[key] : key.substring(0, 1).toLocaleLowerCase();
        result.push(`${preciseDiff[key]}${keyName}`);
      }
      dateOrderIndex++;
    }
    if (result.length) {
      return `${result.join(' ')} ago`;
    }
    return 'just now';
  }
  return '-';
}

interface StoragesWithTruckSvg extends FracStorage {
  truckCount: number;
}

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'sa-lmo-frac-home-storage-prediction',
  styleUrls: ['./lmo-frac-home-storage-prediction.component.scss'],
  templateUrl: './lmo-frac-home-storage-prediction.component.html',
})
export class LmoFracHomeStoragePredictionComponent implements OnInit {
  private _fracGraph: FracGraphSocketUpdate;
  public loadPluralMapping = loadPluralMapping;
  public storages: StoragesWithTruckSvg[] = [];
  @Input() public set fracGraph(update: FracGraphSocketUpdate) {
    this._fracGraph = update;
    if (this._fracGraph && this._fracGraph.storages) {
      this.setupStorages();
    }
  }

  public get fracGraph() {
    return this._fracGraph;
  }
  @Input() public frac: Frac;

  constructor() {}

  public ngOnInit() {}

  public trackByMeshId(_index: number, storage: FracStorage): number {
    return storage.mesh.id;
  }

  public loadsToMax(capacity: number, currentStock: number, loadWeight: number): number {
    return Math.floor((capacity - currentStock) / loadWeight);
  }

  public fromNow(dateAsString: string): string {
    const asMoment = moment(dateAsString);
    if (asMoment.isValid) {
      const preciseDiff: PreciseDiff = (moment as any).preciseDiff(moment(), asMoment, true);
      if (preciseDiff.firstDateWasLater) {
        return 'Now';
      }

      if (preciseDiff.minutes > 31) {
        return `${preciseDiff.hours + 1}.0h from Now`;
      }
      if (preciseDiff.minutes > 1) {
        return `${preciseDiff.hours}.5h from Now`;
      }
      return 'Now';
    }
    return '-';
  }

  public setupStorages() {
    this.storages = this.fracGraph.storages
      .map((storage) => {
        if (storage.currentStocks) {
          const truckCount = storage.currentStocks.reduce((sum, stock) => sum + stock.deliveryCountExisting, 0);
          return {
            ...storage,
            truckCount,
          };
        }
        return {
          ...storage,
          truckCount: 0,
        };
      })
      .filter((storage) => storage.mesh && storage.mesh.id);
  }

  public isBox(storage: FracStorage): boolean {
    return storage.storageType.name === 'Box';
  }

  public lastUpdateTime(storage: StoragesWithTruckSvg): string {
    let str = `Stock last updated: ${ago(storage.updatedAt)}`;
    if (storage.updatedBy) {
      if (storage.updatedBy.includes('SANDi')) {
        str = `${str} by API`;
      } else {
        str = `${str} by ${storage.updatedBy}`;
      }
    }
    return str;
  }
}

interface PreciseDiff {
  days: number;
  firstDateWasLater: boolean;
  hours: number;
  minutes: number;
  months: number;
  seconds: number;
  years: number;
}
